import * as React from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, useMediaQuery, useTheme } from '@mui/material';
import SessionContext, { type Session } from './SessionContext';
import './App.css';
import { createAppTheme } from './theme';
import { useEffect, useRef } from 'react';
import { onAuthStateChanged } from './firebase/auth';
import { User } from 'firebase/auth';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { ConfirmProvider } from 'material-ui-confirm';
import { setIsOnPhone, setUser } from './store/slices/generalSlice';
import { SnackbarProvider } from './snackbarcontext';
import LinearProgress from '@mui/material/LinearProgress';
import { store } from './store';
import LoadingPage from './layouts/components/LoadingPage';
import useCapacitorDeepLinks from './UniversalLinks';
import { addTokenReceivedListener, addNotificationReceivedListener, removeAllDeliveredNotifications } from './firebaseMessagingUtils';
import { updateStatusBarStyle } from './utils/statusBarUtils';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { checkForNewerVersion } from './api/pelaajaporssiApi';
import { IAppStatusCheck } from './types';
import AppStatusDialog from './components/AppStatusDialog';
import dayjs from 'dayjs';

export default function App() {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const outletRef = useRef<HTMLDivElement>(null);
  const [outletInitialized, setOutletInitialized] = React.useState(false);
  const [appStatusDialog, setAppStatusDialog] = React.useState<{
    open: boolean;
    title: string;
    message: string;
    buttonText?: string;
    buttonLink?: string;
    dismissable: boolean;
    isBlocking: boolean;
  } | null>(null);
  const userData = useAppSelector(state => state.general?.userData);

  const dispatch = useAppDispatch();
  const location = useLocation();

  const dark = createAppTheme('dark');
  const light = createAppTheme('light');

  useCapacitorDeepLinks();

  const handleAppStatusAlert = (appStatus: IAppStatusCheck) => {
    // If app is disabled, show blocking message
    if (!appStatus.enabled) {
      setAppStatusDialog({
        open: true,
        title: appStatus.enabledMsg?.title || "Sovellus ei ole käytettävissä",
        message: appStatus.enabledMsg?.msg || "Sovellus on väliaikaisesti poissa käytöstä. Yritä myöhemmin uudelleen.",
        buttonLink: appStatus.enabledMsg?.btnLink,
        buttonText: appStatus.enabledMsg?.btn,
        dismissable: false,
        isBlocking: true
      });
      return;
    }

    // If there's a major message, show blocking dialog
    if (appStatus.majorMsg) {
      setAppStatusDialog({
        open: true,
        title: appStatus.majorMsg.title,
        message: appStatus.majorMsg.msg,
        buttonText: appStatus.majorMsg.btn,
        buttonLink: appStatus.majorMsg.btnLink,
        dismissable: appStatus.majorMsg.dismissable,
        isBlocking: true
      });
      return;
    }

    // If there's a minor message, show dismissable dialog
    if (appStatus.minorMsg) {
      const lastTimeDismissed = localStorage.getItem('ilmo_lastTimeMinorMsgDismissedTime')
      if (lastTimeDismissed) {
        const lastTimeDismissedDate = dayjs(lastTimeDismissed)
        const now = dayjs()
        const diffDays = now.diff(lastTimeDismissedDate, 'day')
        if (diffDays > 3) {
          setAppStatusDialog({
            open: true,
            title: appStatus.minorMsg.title,
            message: appStatus.minorMsg.msg,
            buttonText: appStatus.minorMsg.btn,
            buttonLink: appStatus.minorMsg.btnLink,
            dismissable: appStatus.minorMsg.dismissable,
            isBlocking: false
          });
        }
      } else {
        setAppStatusDialog({
          open: true,
          title: appStatus.minorMsg.title,
          message: appStatus.minorMsg.msg,
          buttonText: appStatus.minorMsg.btn,
          buttonLink: appStatus.minorMsg.btnLink,
          dismissable: appStatus.minorMsg.dismissable,
          isBlocking: false
        });
      }
    }
  };

  const handleDialogClose = () => {
    setAppStatusDialog(null);
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      removeAllDeliveredNotifications();
    }
  }, [location]);

  const handleDialogUpdate = () => {
    // Close dialog only if it's dismissable
    if (appStatusDialog?.dismissable) {
      setAppStatusDialog(null);
    }
  };

  const checkForVersion = async () => {
    const v = `${process.env.NATIVE_APP_VERSION}`;
    const checkForVersion: IAppStatusCheck = await checkForNewerVersion(v);
    handleAppStatusAlert(checkForVersion);
  }

  useEffect(() => {
    checkForVersion();
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupAuthListener = async () => {
      unsubscribe = await onAuthStateChanged((user: User | null) => {
        if (user) {
          setSession({
            user: {
              name: user.displayName || '',
              email: user.email || '',
              image: user.photoURL || '',
            },
          });
          const userData = store.getState().general.userData;
          if (!userData) {
            dispatch(setUser({
              uid: user.uid,
              email: user.email || '',
              emailVerified: user.emailVerified,
              displayName: user.displayName || '',
              bio: "",
              justCreated: false,
              photoURL: user.photoURL || '',
              disabled: false,
              metadata: {
                lastSignInTime: user.metadata.lastSignInTime || '',
                creationTime: user.metadata.creationTime || '',
                lastRefreshTime: "",
              },
              tokensValidAfterTime: "",
              providerData: user.providerData,
            }));
          }
        } else {
          setSession(null);
          dispatch(setUser(undefined));
        }
        setLoading(false);
      });
    };

    setupAuthListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    console.log('addTokenReceivedListener');
    addTokenReceivedListener();
    addNotificationReceivedListener();
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() !== "android") {
      const setupSafeArea = async () => {
        await SafeArea.getSafeAreaInsets().then(({ insets }) => {
          const styleEl = document.createElement('style');
          styleEl.innerHTML = `
            :root {
              --safe-area-inset-top: ${insets.top}px;
              --safe-area-inset-bottom: ${insets.bottom}px;
              --safe-area-inset-left: ${insets.left}px;
              --safe-area-inset-right: ${insets.right}px;
            }
          `;
          document.head.appendChild(styleEl);
          document.body.style.setProperty(
            `padding-left`,
            `${insets.left}px`,
          );
          document.body.style.setProperty(
            `padding-right`,
            `${insets.right}px`,
          );
        });

        await SafeArea.getStatusBarHeight().then(({ statusBarHeight }) => {
          document.documentElement.style.setProperty(
            `--status-bar-height`,
            `${statusBarHeight}px`,
          );
        });

        await SafeArea.addListener('safeAreaChanged', data => {
          const { insets } = data;
          for (const [key, value] of Object.entries(insets)) {
            document.documentElement.style.setProperty(
              `--safe-area-inset-${key}`,
              `${value}px`,
            );
          }
        });
      }
      setupSafeArea();
      return () => {
        SafeArea.removeAllListeners();
      }
    }
  }, []);

  useEffect(() => {
    const checkOutletHeight = () => {
      if (outletRef.current && outletRef.current.offsetHeight === 0) {
        window.location.reload();
      } else if (outletRef.current && outletRef.current.offsetHeight > 0) {
        if (!outletInitialized) {
          setOutletInitialized(true);
        }
      }
    };
    checkOutletHeight();
    const interval = setInterval(checkOutletHeight, 1000);
    const timeout = setTimeout(() => clearInterval(interval), 5000);

    const isOnPhone = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    dispatch(setIsOnPhone(isOnPhone));

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    updateStatusBarStyle(userData?.darkMode || false);
  }, [userData?.darkMode]);

  const sessionContextValue = React.useMemo(
    () => ({
      session,
      setSession,
      loading,
    }),
    [session, loading],
  );

  if (loading) {
    return (
      <ThemeProvider theme={userData?.darkMode ? dark : light}>
        <CssBaseline />
        <LoadingPage />
      </ThemeProvider>
    );
  }

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <ThemeProvider theme={userData?.darkMode ? dark : light}>
        <CssBaseline />
        <ConfirmProvider defaultOptions={{
          confirmationText: "Kyllä",
          cancellationText: "Peruuta",
        }}>
          <SnackbarProvider position="bottom-right">
            <div ref={outletRef} style={{ ...appStatusDialog?.open && appStatusDialog.isBlocking && { height: "1px" } }}>
              {/* Render app content only if not blocking */}
              {!appStatusDialog?.isBlocking && <Outlet />}

              {/* App Status Dialog */}
              <AppStatusDialog
                open={appStatusDialog?.open || false}
                title={appStatusDialog?.title || ""}
                message={appStatusDialog?.message || ""}
                buttonText={appStatusDialog?.buttonText}
                buttonLink={appStatusDialog?.buttonLink}
                dismissable={appStatusDialog?.dismissable || false}
                isBlocking={appStatusDialog?.isBlocking || false}
                onClose={handleDialogClose}
                onUpdate={handleDialogUpdate}
              />
            </div>
          </SnackbarProvider>
        </ConfirmProvider>
        <ScrollRestoration
          getKey={(location, matches) => {
            const screenWidth = window.innerWidth;
            const isXs = screenWidth < 600;
            if (location.pathname.includes('/clubs/') && (isXs || location.pathname.includes('/chat') || location.pathname.includes('/club-settings'))) {
              const clubId = location.pathname.split('/')[2];
              return `club/${clubId.trim()}`;
            }
            if (location.pathname.includes('/home') && !location.pathname.includes('events')) {
              return 'home';
            }
            if (location.pathname.includes('events') && !location.pathname.includes('create') && isXs) {
              console.log("returning events");
              return 'events';
            }
            return location.pathname;
          }}
        />
      </ThemeProvider>
    </SessionContext.Provider >
  );
}
